import React from 'react';

interface SeamCircleSvgProps {
  seamDegree?: number;
  seamValueMm?: number | null;
  referenceDegree?: number;
  seamExclusion?: number;
  exclusionCentreDegree?: number;
  sixOClockValue?: number;
  showBump?: boolean;
  seamCapWidth?: number;
  pipeDiameter?: number;
  size?: number;
  showSeamExclusionZone?: boolean;
  showSixOClockZone?: boolean;
  /** Dashed arc from reference to seam along outer ring (tablet optimum gauge). */
  showRotationArc?: boolean;
  /** `tablet` matches field device: red ring, yellow inner ring, blue needle, white pill label. */
  visualVariant?: 'legacy' | 'tablet';
}

const SeamCircleSvg: React.FC<SeamCircleSvgProps> = ({
  seamDegree = 0,
  seamValueMm = null,
  referenceDegree = 0,
  seamExclusion = 0,
  exclusionCentreDegree,
  sixOClockValue = 0,
  showBump = true,
  seamCapWidth = 0,
  pipeDiameter = 0,
  size = 240,
  showSeamExclusionZone = true,
  showSixOClockZone = true,
  showRotationArc = false,
  visualVariant = 'tablet',
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 40;

  const toXY = (angleDeg: number, r: number) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const centre = exclusionCentreDegree ?? seamDegree;
  const blueAngle = centre - seamExclusion;
  const redAngle = centre + seamExclusion;
  const greyAngle = 180 - sixOClockValue;
  const greenAngle = 180 + sixOClockValue;

  const bumpPath = (() => {
    if (!showBump || !seamCapWidth || !pipeDiameter) return null;
    const scale = pipeDiameter > 0 ? radius / (pipeDiameter / 2) : 0;
    const br = (seamCapWidth * scale) / 2;
    if (br <= 0) return null;
    const bca = seamDegree - 90;
    const bcx = cx + radius * Math.cos((bca * Math.PI) / 180);
    const bcy = cy + radius * Math.sin((bca * Math.PI) / 180);
    const sx = bcx + br * Math.cos(((bca - 90) * Math.PI) / 180);
    const sy = bcy + br * Math.sin(((bca - 90) * Math.PI) / 180);
    const ex = bcx + br * Math.cos(((bca + 90) * Math.PI) / 180);
    const ey = bcy + br * Math.sin(((bca + 90) * Math.PI) / 180);
    return `M ${sx} ${sy} A ${br} ${br} 0 0 1 ${ex} ${ey} L ${bcx} ${bcy} Z`;
  })();

  const outerArcR = radius * 1.1;
  const arcPath = (() => {
    const s = toXY(referenceDegree, outerArcR);
    const e = toXY(seamDegree, outerArcR);
    const sa = referenceDegree - 90;
    const ea = seamDegree - 90;
    const delta = ((ea - sa + 540) % 360) - 180;
    const large = Math.abs(delta) > 180 ? 1 : 0;
    const sweep = delta > 0 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${outerArcR} ${outerArcR} 0 ${large} ${sweep} ${e.x} ${e.y}`;
  })();

  const formatMmLabel = (mm: number) => {
    const rounded = Math.round(mm * 100) / 100;
    const s = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    return `${s} mm`;
  };

  const displayLabel =
    seamValueMm !== null && seamValueMm !== undefined
      ? formatMmLabel(Number(seamValueMm))
      : '0 mm';

  const pink = toXY(referenceDegree, radius * 1.3);
  const orange = toXY(seamDegree, radius * 1.3);
  const seamRing = toXY(seamDegree, radius);
  const needleInner = toXY(seamDegree, radius * 0.12);
  const blue = toXY(blueAngle, radius);
  const red = toXY(redAngle, radius);
  const yellow = toXY(180, radius);
  const grey = toXY(greyAngle, radius);
  const greenPt = toXY(greenAngle, radius);
  const vb = `${size * -0.1} ${size * -0.1} ${size * 1.2} ${size * 1.2}`;

  const labelPos = (() => {
    const lr = radius * 1.34;
    return toXY(seamDegree, lr);
  })();

  const pillW = Math.min(112, Math.max(52, displayLabel.length * 7 + 20));
  const pillH = 22;

  const fillZone = (a1: number, a2: number, k: string) => {
    let lo = a1;
    let hi = a2;
    if (lo > hi) [lo, hi] = [hi, lo];
    return Array.from({ length: 81 }, (_, i) => {
      const p = toXY(lo + ((hi - lo) * i) / 80, radius);
      return (
        <line
          key={`${k}-${i}`}
          x1={cx}
          y1={cy}
          x2={p.x}
          y2={p.y}
          stroke="#9ca3af"
          strokeWidth={1.5}
          strokeDasharray="3"
          opacity={0.45}
        />
      );
    });
  };

  const sixOClockWedgePath = (() => {
    if (!showSixOClockZone || sixOClockValue <= 0) return null;
    const p1 = toXY(greyAngle, radius);
    const p2 = toXY(greenAngle, radius);
    return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${radius} ${radius} 0 0 1 ${p2.x} ${p2.y} Z`;
  })();

  const seamExclusionWedgePath = (() => {
    if (!showSeamExclusionZone || seamExclusion <= 0) return null;
    const p1 = toXY(blueAngle, radius);
    const p2 = toXY(redAngle, radius);
    const useLarge = Math.abs(redAngle - blueAngle) > 180 ? 1 : 0;
    const sweep = redAngle >= blueAngle ? 1 : 0;
    return `M ${cx} ${cy} L ${p1.x} ${p1.y} A ${radius} ${radius} 0 ${useLarge} ${sweep} ${p2.x} ${p2.y} Z`;
  })();

  if (visualVariant === 'tablet') {
    const innerRingR = radius - 5;
    /** Field tablet: thick red outer ring, amber inner ring, wedges, blue needle, red dot, red dashed rotation arc. */
    return (
      <svg width={size} height={size} viewBox={vb}>
        <rect x={size * -0.1} y={size * -0.1} width={size * 1.2} height={size * 1.2} fill="transparent" rx={18} />
        {seamExclusionWedgePath && (
          <path d={seamExclusionWedgePath} fill="#bfdbfe" fillOpacity={0.22} stroke="none" />
        )}
        {sixOClockWedgePath && <path d={sixOClockWedgePath} fill="#d1d5db" fillOpacity={0.5} stroke="none" />}
        <circle cx={cx} cy={cy} r={innerRingR} stroke="#fbbf24" strokeWidth={2} fill="none" />
        <circle cx={cx} cy={cy} r={radius} stroke="#ef4444" strokeWidth={6} fill="none" />
        {bumpPath && <path d={bumpPath} fill="#ef4444" stroke="#ef4444" strokeWidth={2} />}
        {showRotationArc && (
          <path
            d={arcPath}
            stroke="#ef4444"
            fill="none"
            strokeWidth={2.25}
            strokeDasharray="7 6"
            opacity={0.95}
          />
        )}
        <line
          x1={cx}
          y1={cy}
          x2={toXY(referenceDegree, radius * 0.92).x}
          y2={toXY(referenceDegree, radius * 0.92).y}
          stroke="#e2e8f0"
          strokeWidth={1.5}
        />
        <line
          x1={needleInner.x}
          y1={needleInner.y}
          x2={seamRing.x}
          y2={seamRing.y}
          stroke="#2563eb"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={seamRing.x} cy={seamRing.y} r={5} fill="#ef4444" stroke="#ffffff" strokeWidth={1.5} />
        <g>
          <rect
            x={labelPos.x - pillW / 2}
            y={labelPos.y - pillH / 2}
            width={pillW}
            height={pillH}
            rx={pillH / 2}
            fill="#ffffff"
            stroke="#e5e7eb"
            strokeWidth={1.25}
          />
          <text
            x={labelPos.x}
            y={labelPos.y + 1}
            fill="#111827"
            fontSize={12}
            fontWeight={700}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {displayLabel}
          </text>
        </g>
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox={vb}>
      <rect x={size * -0.1} y={size * -0.1} width={size * 1.2} height={size * 1.2} fill="transparent" rx={18} />
      {showSeamExclusionZone && seamExclusion > 0 && fillZone(blueAngle, redAngle, 'exc')}
      {showSixOClockZone && sixOClockValue > 0 && fillZone(greyAngle, greenAngle, 'six')}
      <circle cx={cx} cy={cy} r={radius} stroke="#ef4444" strokeWidth={5} fill="none" />
      {bumpPath && <path d={bumpPath} fill="#ef4444" stroke="#ef4444" strokeWidth={2} />}
      <line x1={cx} y1={cy} x2={pink.x} y2={pink.y} stroke="#f9a8d4" strokeWidth={3} />
      <line x1={cx} y1={cy} x2={orange.x} y2={orange.y} stroke="#fb923c" strokeWidth={3} />
      {showSeamExclusionZone && seamExclusion > 0 && (
        <>
          <line x1={cx} y1={cy} x2={blue.x} y2={blue.y} stroke="#60a5fa" strokeWidth={3} />
          <line x1={cx} y1={cy} x2={red.x} y2={red.y} stroke="#f87171" strokeWidth={3} />
        </>
      )}
      {showSixOClockZone && sixOClockValue > 0 && (
        <>
          <line x1={cx} y1={cy} x2={yellow.x} y2={yellow.y} stroke="#facc15" strokeWidth={3} />
          <line x1={cx} y1={cy} x2={grey.x} y2={grey.y} stroke="#9ca3af" strokeWidth={3} />
          <line x1={cx} y1={cy} x2={greenPt.x} y2={greenPt.y} stroke="#4ade80" strokeWidth={3} />
        </>
      )}
      {showRotationArc && (
        <path d={arcPath} stroke="#374151" fill="none" strokeWidth={3} strokeDasharray="10,5" />
      )}
      <circle cx={seamRing.x} cy={seamRing.y} r={6} fill="#ef4444" stroke="#fff" strokeWidth={1.5} />
      <text x={labelPos.x} y={labelPos.y} fill="#1f2937" fontSize={12} textAnchor="middle" dominantBaseline="middle">
        {displayLabel}
      </text>
    </svg>
  );
};

export default SeamCircleSvg;
